import { useMemo } from 'react';
import { Tabs, Alert } from 'antd';
import type { TabsProps } from 'antd';
import { useSearchParams } from 'react-router-dom';
import { useSizSections, ppeSectionKeyLabels, type PpeSectionKey } from '@/entities/siz';
import { PageSpinner } from '@/shared/ui/PageSpinner';
import { NormsTab } from './NormsTab';
import { ProcurementTab } from './ProcurementTab';
import { CatalogTab } from './CatalogTab';
import { EmployeesTab } from './EmployeesTab';
import { MyPpeTab } from './MyPpeTab';

const SECTION_CONTENT: Record<PpeSectionKey, React.ReactNode> = {
  my_ppe: <MyPpeTab />,
  employees_ppe: <EmployeesTab />,
  norms_catalog: <NormsTab />,
  siz_catalog: <CatalogTab />,
  reports: <ProcurementTab />,
};

export function Siz() {
  const [params, setParams] = useSearchParams();
  const tabFromUrl = params.get('tab') ?? '';

  const { data: sections, isPending, isError } = useSizSections();

  const visibleTabs = useMemo<NonNullable<TabsProps['items']>>(() => {
    if (!sections) return [];
    return sections
      .filter((s): s is { key: PpeSectionKey } => s.key in SECTION_CONTENT)
      .map(({ key }) => ({
        key,
        label: ppeSectionKeyLabels[key],
        children: SECTION_CONTENT[key],
      }));
  }, [sections]);

  const firstTabKey = visibleTabs[0]?.key ?? '';

  const activeKey = useMemo(() => {
    if (tabFromUrl && visibleTabs.some((t) => t.key === tabFromUrl)) return tabFromUrl;
    return firstTabKey;
  }, [tabFromUrl, visibleTabs, firstTabKey]);

  const handleTabChange = (key: string) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', key);
      return next;
    });
  };

  if (isPending) return <PageSpinner />;
  if (isError) return <Alert type="error" showIcon message="Не удалось загрузить доступные подразделы" />;
  if (visibleTabs.length === 0) {
    return <Alert type="info" showIcon message="Нет доступных подразделов" />;
  }

  return <Tabs activeKey={activeKey} onChange={handleTabChange} items={visibleTabs} />;
}
